/**
 * 全屏编辑器的上下文快照。
 *
 * 独立标签页里没有多维表格 SDK：拿不到字段、拿不到记录值，什么都渲染不出来。
 * 所以由侧边栏在打开前把「这张表长什么样 + 当前这条记录的渲染数据」存一份到
 * 服务端（1 小时过期），新标签页凭 accountId 取回来。
 *
 * 模板本身走的是云同步（tpl:<accountId>），不放在这里 —— 那边是双向的，
 * 编辑器改完要推回去让侧边栏看到。
 */
import type { FieldMeta } from '../api/types';
import type { ContractRenderData } from '../contract/types';

const API_BASE = 'https://printpro.xiaomiao.win';

export interface FsContext {
  tableId: string;
  recordId: string | null;
  templateId: string;
  fieldMetas: FieldMeta[];
  /** 当前记录的渲染数据快照。附件链接是短时效的，过期了画布上就是破图，可接受。 */
  data: ContractRenderData | null;
  tableName?: string;
  /** 企业标识，独立页里没有 SDK 拿不到，靠这里带过去才能拉团队共享模板。 */
  tenantKey?: string;
  /** 每张关联表的字段列表（tableId → 字段）。独立页没有 SDK，配明细表全靠它。 */
  linkedFieldMetas?: Record<string, FieldMeta[]>;
  /**
   * 个人 Base 令牌 + Base id：只写进快照给 worker 用（/api/att/live 按文件 token
   * 现取下载地址），GET 快照时 worker 会把令牌抹掉，不下发到页面。
   */
  personalBaseToken?: string;
  baseId?: string;
}

/**
 * 把快照里所有附件的地址改写成「按 token 现取」的代理地址。
 *
 * 飞书给的临时下载地址几分钟就过期；侧边栏有 SDK 能随时重取，独立页没有 ——
 * 过期后画布上全是破图。改走 worker：它用快照里的令牌现取地址再中转。
 */
export function rewriteAttachmentUrls(data: ContractRenderData, accountId: string): ContractRenderData {
  const live = (it: { url: string; token?: string; ref?: { tableId: string; fieldId: string; recordId: string }; name: string }) => {
    if (!it.token || !it.ref) return it;
    const q = new URLSearchParams({
      account: accountId, t: it.ref.tableId, f: it.ref.fieldId, r: it.ref.recordId, k: it.token, n: it.name,
    });
    return { ...it, url: `${API_BASE}/api/att/live?${q.toString()}` };
  };
  const mapItems = <T extends Record<string, unknown[]>>(m: T | undefined): T | undefined => {
    if (!m) return m;
    const out: Record<string, unknown[]> = {};
    for (const [k, arr] of Object.entries(m)) out[k] = (arr as Parameters<typeof live>[0][]).map(live);
    return out as T;
  };
  const d = data as unknown as {
    attachments?: Record<string, Parameters<typeof live>[0][]>;
    mainAttachments?: Record<string, Parameters<typeof live>[0][]>;
    linkedRecords?: Record<string, { records: Array<{ attachments?: Record<string, Parameters<typeof live>[0][]> }> }>;
  };
  const linked: typeof d.linkedRecords = {};
  for (const [k, t] of Object.entries(d.linkedRecords ?? {})) {
    linked[k] = { ...t, records: t.records.map((r) => ({ ...r, attachments: mapItems(r.attachments) })) };
  }
  return {
    ...data,
    attachments: mapItems(d.attachments) ?? {},
    mainAttachments: mapItems(d.mainAttachments),
    linkedRecords: linked,
  } as ContractRenderData;
}

export async function putFsContext(accountId: string, context: FsContext): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/fscontext`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountId, context }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function getFsContext(accountId: string): Promise<FsContext | null> {
  try {
    const res = await fetch(
      `${API_BASE}/api/fscontext?accountId=${encodeURIComponent(accountId)}`,
    );
    if (!res.ok) return null;
    const body = (await res.json()) as { context?: FsContext } | FsContext;
    const ctx = (body as { context?: FsContext }).context ?? (body as FsContext);
    return ctx && typeof ctx === 'object' && Array.isArray(ctx.fieldMetas) ? ctx : null;
  } catch {
    return null;
  }
}
